import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import api from '../lib/api'
import TopBar from '../components/TopBar'
import WatchlistPanel from '../components/WatchlistPanel'
import SignalFeed from '../components/SignalFeed'
import VerdictTicker from '../components/VerdictTicker'
import DeliberationTheatre from '../components/DeliberationTheatre'
import MobileSwipeContainer from '../components/MobileSwipeContainer'
import '../styles/tokens.css'
import '../styles/panels.css'

// Fallback demo verdicts when API unavailable (matches seed_verdicts.py)
const DEMO_VERDICTS = [
  { ticker: 'NVDA', direction: 'FOR', confidence: 78, timestamp: Date.now() - 3600000, outcome: 'correct' },
  { ticker: 'FED', direction: 'AGAINST', confidence: 85, timestamp: Date.now() - 7200000, outcome: 'correct' },
  { ticker: 'BTC', direction: 'FOR', confidence: 72, timestamp: Date.now() - 10800000, outcome: 'wrong' },
  { ticker: 'TSLA', direction: 'FOR', confidence: 71, timestamp: Date.now() - 14400000, outcome: 'wrong' },
  { ticker: 'CHIEFS', direction: 'FOR', confidence: 68, timestamp: Date.now() - 18000000, outcome: 'correct' },
  { ticker: 'AMD', direction: 'AGAINST', confidence: 73, timestamp: Date.now() - 21600000, outcome: 'correct' },
  { ticker: 'OIL', direction: 'FOR', confidence: 65, timestamp: Date.now() - 25200000, outcome: 'wrong' },
  { ticker: 'META', direction: 'FOR', confidence: 77, timestamp: Date.now() - 28800000, outcome: 'correct' },
  { ticker: 'YIELD', direction: 'FOR', confidence: 81, timestamp: Date.now() - 32400000, outcome: 'wrong' },
  { ticker: 'AAPL', direction: 'AGAINST', confidence: 69, timestamp: Date.now() - 36000000, outcome: 'correct' },
]

// Fallback breach alerts - real incidents from January 2026
const DEMO_ALERTS = [
  { id: 1, ticker: 'NSANY', type: 'breach', message: 'Nissan: Everest ransomware group claims 900GB data exfiltration', severity: 'high', timestamp: Date.now() - 3600000 },
  { id: 2, ticker: 'ESA', type: 'breach', message: 'European Space Agency: 700GB+ data exposed across two incidents', severity: 'high', timestamp: Date.now() - 86400000 },
  { id: 3, ticker: 'ELECF', type: 'breach', message: 'Endesa (Spain): Unauthorized access to customer billing data', severity: 'medium', timestamp: Date.now() - 172800000 },
  { id: 4, ticker: 'HEALTH', type: 'breach', message: 'Manage My Health: 125K users medical records potentially exposed', severity: 'high', timestamp: Date.now() - 259200000 },
  { id: 5, ticker: 'AERO', type: 'breach', message: 'Aero-Coating GmbH: Qilin ransomware, data leaked on dark web', severity: 'high', timestamp: Date.now() - 345600000 },
  { id: 6, ticker: 'LEGAL', type: 'breach', message: 'Bergmanis Preyra LLP: Client files exposed by Qilin ransomware', severity: 'high', timestamp: Date.now() - 432000000 },
  { id: 7, ticker: 'EDU', type: 'breach', message: 'Monroe University: 320K individuals affected, PII compromised', severity: 'medium', timestamp: Date.now() - 518400000 },
  { id: 8, ticker: 'EDU', type: 'breach', message: 'Baker University: 53K victims, financial account info exposed', severity: 'medium', timestamp: Date.now() - 604800000 },
  { id: 9, ticker: 'SKIN', type: 'breach', message: 'Brevard Skin & Cancer: 55K patient health records at risk', severity: 'high', timestamp: Date.now() - 691200000 },
  { id: 10, ticker: 'FORUM', type: 'breach', message: 'BreachForums hacked: 324K hacker accounts leaked including IPs', severity: 'medium', timestamp: Date.now() - 777600000 },
]

export default function Dashboard() {
  const { user } = useAuth()
  const [signals, setSignals] = useState([])
  const [watchlist, setWatchlist] = useState(['NVDA', 'AAPL', 'TSLA', 'META', 'GOOGL'])
  const [recentVerdicts, setRecentVerdicts] = useState(DEMO_VERDICTS)
  const [breachAlerts, setBreachAlerts] = useState(DEMO_ALERTS)
  const [mobilePanel, setMobilePanel] = useState(1) // 0=watchlist, 1=judge, 2=feed
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  // Track mobile vs desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const [stats, setStats] = useState({
    signalsCollected: 0,
    signals24h: 0,
    sourcesActive: 0,
    sourcesTotal: 34,
    nextBriefing: null
  })

  // Fetch real data from API
  useEffect(() => {
    // Fetch track record (public endpoint)
    api.getTrackRecord()
      .then(data => {
        // Convert track record verdicts to ticker format for the bottom bar
        const verdicts = (data.recent_verdicts || []).map((v, i) => {
          // Extract ticker from query (e.g., "NVDA outlook" -> "NVDA")
          const tickerMatch = v.query.match(/\b([A-Z]{2,5})\b/)
          const ticker = tickerMatch ? tickerMatch[1] : v.query.split(' ')[0].toUpperCase()
          return {
            ticker,
            direction: (v.position === 'bullish' || v.position === 'for') ? 'FOR' : (v.position === 'bearish' || v.position === 'against') ? 'AGAINST' : 'NEUTRAL',
            confidence: Math.round(v.confidence * 100),
            timestamp: data.generated_at ? new Date(data.generated_at).getTime() - (i * 60 * 60 * 1000) : Date.now() - (i * 60 * 60 * 1000),
            outcome: v.outcome
          }
        })
        // Only update if we have real verdicts, otherwise keep demo data
        if (verdicts.length > 0) {
          setRecentVerdicts(verdicts)
        }

        // Update stats with real verdict count
        setStats(prev => ({
          ...prev,
          totalVerdicts: data.overall?.total_verdicts || 0,
          accuracyRate: data.overall?.accuracy_rate || 0
        }))
      })
      .catch(err => console.warn('Track record fetch failed:', err))

    // Fetch live stats from API
    const fetchStats = () => {
      api.getStats()
        .then(data => {
          setStats(prev => ({
            ...prev,
            signalsCollected: data.signals_count || 0,
            signals24h: data.signals_24h || 0,
            sourcesActive: data.sources_active || 0,
            sourcesTotal: data.collectors_total || 34,
            nextBriefing: getNextBriefingTime()
          }))
        })
        .catch(err => console.warn('Stats fetch failed:', err))
    }

    fetchStats()
    const statsInterval = setInterval(fetchStats, 30000) // Poll every 30s

    // Fetch real signals from API
    const fetchSignals = () => {
      api.getSignals(20)
        .then(data => {
          const formatted = data.map(s => ({
            id: s.id,
            source: s.source.charAt(0).toUpperCase() + s.source.slice(1),
            headline: s.title,
            tickers: s.entities || [],
            timestamp: new Date(s.timestamp).getTime(),
            credibility: s.credibility
          }))
          setSignals(formatted)
        })
        .catch(err => console.warn('Signals fetch failed:', err))
    }

    fetchSignals()

    // Refresh signals every 30 seconds
    const signalInterval = setInterval(fetchSignals, 30000)

    // Fetch real alerts from API
    const fetchAlerts = () => {
      api.getAlerts(10)
        .then(data => {
          // Filter out fake EPA "PRODUCTION HALTED" alerts
          const validAlerts = data.filter(a =>
            !a.message?.includes('PRODUCTION HALTED') &&
            !a.message?.includes('-100%')
          )
          // Only update if we have real alerts, otherwise keep demo data
          if (validAlerts.length > 0) {
            const formatted = validAlerts.map(a => ({
              id: a.id,
              ticker: a.ticker,
              type: a.type,
              message: a.message,
              severity: a.severity,
              timestamp: new Date(a.timestamp).getTime()
            }))
            setBreachAlerts(formatted)
          }
        })
        .catch(err => console.warn('Alerts fetch failed:', err))
    }

    fetchAlerts()

    // Refresh alerts every 60 seconds
    const alertInterval = setInterval(fetchAlerts, 60000)

    // Refresh track record every 5 minutes
    const trackRecordInterval = setInterval(() => {
      api.getTrackRecord().catch(() => {})
      setStats(prev => ({
        ...prev,
        nextBriefing: getNextBriefingTime()
      }))
    }, 300000)

    return () => {
      clearInterval(signalInterval)
      clearInterval(alertInterval)
      clearInterval(trackRecordInterval)
      clearInterval(statsInterval)
    }
  }, [])

  function getNextBriefingTime() {
    const now = new Date()
    const target = new Date()
    target.setHours(8, 30, 0, 0) // 8:30 AM ET

    if (now > target) {
      target.setDate(target.getDate() + 1)
    }

    return target.getTime()
  }

  const handleAddToWatchlist = (ticker) => {
    if (!watchlist.includes(ticker.toUpperCase())) {
      setWatchlist([...watchlist, ticker.toUpperCase()])
    }
  }

  const handleRemoveFromWatchlist = (ticker) => {
    setWatchlist(watchlist.filter(t => t !== ticker))
  }

  return (
    <div className="trader-desk">
      {/* Top Bar */}
      <TopBar stats={stats} user={user} />

      {/* Upgrade CTA for free tier */}
      {user?.tier === 'free' && (
        <div className="upgrade-banner">
          <a href="/pricing" className="upgrade-cta">
            <span className="upgrade-text">Trial mode — Upgrade to Professional for full deliberation access</span>
            <span className="upgrade-arrow">→</span>
          </a>
        </div>
      )}

      {/* Mobile Layout with Swipe */}
      {isMobile ? (
        <>
          {/* Mobile Header Pills */}
          <div className="mobile-header-pills">
            <button
              className={`header-pill ${mobilePanel === 0 ? 'active' : ''}`}
              onClick={() => setMobilePanel(0)}
            >
              Alerts
            </button>
            <button
              className={`header-pill ${mobilePanel === 1 ? 'active' : ''}`}
              onClick={() => setMobilePanel(1)}
            >
              Judge
            </button>
            <button
              className={`header-pill ${mobilePanel === 2 ? 'active' : ''}`}
              onClick={() => setMobilePanel(2)}
            >
              Feed
            </button>
          </div>

          <div className="mobile-panels-wrapper">
            <MobileSwipeContainer
              activeIndex={mobilePanel}
              onIndexChange={setMobilePanel}
            >
              {/* Panel 0: Watchlist & Alerts */}
              <div className="mobile-panel">
                <WatchlistPanel
                  watchlist={watchlist}
                  breachAlerts={breachAlerts}
                  signals={signals}
                  onAdd={handleAddToWatchlist}
                  onRemove={handleRemoveFromWatchlist}
                />
              </div>

              {/* Panel 1: Deliberation Theatre */}
              <div className="mobile-panel">
                <DeliberationTheatre
                  user={user}
                  watchlist={watchlist}
                  onAddToWatchlist={handleAddToWatchlist}
                />
              </div>

              {/* Panel 2: Signal Feed */}
              <div className="mobile-panel">
                <SignalFeed
                  signals={signals}
                  watchlist={watchlist}
                />
              </div>
            </MobileSwipeContainer>
          </div>
        </>
      ) : (
        /* Desktop Three-Panel Layout */
        <div className="panels-container">
          {/* Left Panel - Watchlist & Alerts */}
          <WatchlistPanel
            watchlist={watchlist}
            breachAlerts={breachAlerts}
            signals={signals}
            onAdd={handleAddToWatchlist}
            onRemove={handleRemoveFromWatchlist}
          />

          {/* Center Panel - Deliberation Theatre */}
          <main className="panel-center">
            <DeliberationTheatre
              user={user}
              watchlist={watchlist}
              onAddToWatchlist={handleAddToWatchlist}
            />
          </main>

          {/* Right Panel - Live Signal Feed */}
          <SignalFeed
            signals={signals}
            watchlist={watchlist}
          />
        </div>
      )}

      {/* Bottom Ticker */}
      <VerdictTicker verdicts={recentVerdicts} />
    </div>
  )
}
